import { useCallback, useMemo } from 'react';
import { useStore } from '@nanostores/react';
import { useLocation } from '@/services/router/compat';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { fetchAccountById } from '@/services/auth/account-api';
import { useAuth } from '@/services/auth';
import { AccountState } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { getMasqBarVisibility, announceMasqReadOnlyChange, masqReadOnlyStore } from '@/utils/masq';

const ACCOUNT_LOOKUP_DEBOUNCE_MS = 300;

/**
 * Hook providing masquerade state and actions.
 */
export function useMasq() {
  const auth = useAuth();
  const location = useLocation();
  // The store is the only copy of this flag. The permission check reads it too.
  const readOnly = useStore(masqReadOnlyStore);

  const setReadOnly = useCallback((value: boolean | ((previous: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(masqReadOnlyStore.get()) : value;
    announceMasqReadOnlyChange(newValue);
  }, []);

  const toggleReadOnly = useCallback(() => {
    setReadOnly((previous) => !previous);
  }, [setReadOnly]);

  const isMasqOn = auth.account.is_masq === true;
  const barVisible = getMasqBarVisibility();
  const isAllowed = barVisible && ((auth.account.admin === true && auth.user.can_masquerade === true) || isMasqOn);

  const masqueradeInto = useCallback(
    (accountId: string) => {
      if (isEmptyString(accountId)) return;

      const isWorkspaceRoute = location.pathname.includes('/workspaces/');

      if (isWorkspaceRoute) {
        window.location.href = `/?masq_account_id=${accountId.trim()}`;
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('masq_account_id', accountId.trim());
        window.location.href = url.toString();
      }
    },
    [location.pathname],
  );

  const unmasquerade = useCallback(() => {
    const isWorkspaceRoute = location.pathname.includes('/workspaces/');

    if (isWorkspaceRoute) {
      window.location.href = '/';
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('masq_account_id');
      window.location.href = url.toString();
    }
  }, [location.pathname]);

  return {
    isMasqOn,
    isAllowed,
    readOnly,
    setReadOnly,
    toggleReadOnly,
    masqueradeInto,
    unmasquerade,
  };
}

/**
 * Hook to validate and fetch account info for the masq form.
 * Debounces the input to avoid firing a request on every keystroke.
 */
export function useAccountLookup(accountId: string) {
  const trimmedId = accountId.trim();
  const numericId = Number(trimmedId);
  const isValid = !isEmptyString(trimmedId) && Number.isInteger(numericId) && numericId > 0;

  const [debouncedId] = useDebouncedValue(trimmedId, ACCOUNT_LOOKUP_DEBOUNCE_MS);

  const debouncedNumericId = Number(debouncedId);
  const isDebouncedValid =
    !isEmptyString(debouncedId) && Number.isInteger(debouncedNumericId) && debouncedNumericId > 0;

  const query = useHydraUIAPIQuery({
    queryKey: ['getAccountById', { params: { accountId: debouncedNumericId } }],
    queryFn: () => fetchAccountById(debouncedNumericId),
    queryConfig: {
      enabled: isDebouncedValid,
      retry: false,
      staleTime: 30_000,
      // The payload omits `account` for an id that resolves to nothing usable;
      // the banner already treats a missing result as "no account info".
      select: (data) =>
        data.account == null
          ? null
          : {
              id: data.account.id,
              name: data.account.name,
              dataCenter: data.account.data_center,
            },
    },
  });

  return {
    isValid,
    isLoading: (query.isLoading && isDebouncedValid) || (isValid && trimmedId !== debouncedId),
    accountInfo: query.data ?? null,
    error: query.error,
  };
}

/**
 * Hook to fetch workspace details (shard, identifiers) from `/v1/environments/:id`.
 * The auth payload doesn't include shard, so we need a separate fetch.
 */
export function useWorkspaceDetails(workspaceId: number | undefined) {
  return useHydraUIAPIQuery({
    queryKey: ['getEnvironment', { params: { environmentId: workspaceId ?? 0 } }],
    queryFn: ({ signal }) =>
      hydraUiApi.environments.get({ params: { path: { environment_id: String(workspaceId ?? 0) } }, signal }),
    queryConfig: {
      enabled: workspaceId != null && workspaceId > 0,
      retry: false,
      staleTime: 60_000,
      select: (data) => ({
        shard: data.environment?.shard,
        identifier_type: data.environment?.identifier_type,
        identifiers: data.environment?.identifiers,
        identifiers_migration_in_progress: data.environment?.identifiers_migration_in_progress,
      }),
    },
  });
}

/**
 * Hook to resolve a user ID to an email using the account's user list.
 * Returns the email if found, otherwise the raw ID string.
 */
export function useAccountUsers(accountId: number | undefined) {
  const query = useHydraUIAPIQuery({
    queryKey: ['getAllUsers', { params: { account_id: accountId ?? 0 } }],
    queryFn: ({ signal }) =>
      hydraUiApi.users.getAllUsers({ params: { path: { account_id: String(accountId ?? 0) } }, signal }),
    queryConfig: {
      enabled: accountId != null && accountId > 0,
      retry: false,
      staleTime: 60_000,
    },
  });

  const usersById = useMemo(() => {
    if (query.data?.users == null) return null;
    const map = new Map<string, { email: string; firstName: string; lastName: string }>();
    for (const user of query.data.users) {
      map.set(String(user.user_id), {
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
      });
    }
    return map;
  }, [query.data]);

  return { usersById };
}

/**
 * Hook to check if a workspace has custom SMTP mail servers.
 */
export function useHasMailServers(workspaceId: string | undefined, accountState: string) {
  const skipStates: string[] = [AccountState.Canceled, AccountState.Suspended];

  return useHydraUIAPIQuery({
    queryKey: ['getMailServers', { params: { environment_id: Number(workspaceId ?? 0) } }],
    queryFn: ({ signal }) =>
      hydraUiApi.mailservers.list({ params: { path: { environment_id: String(workspaceId ?? 0) } }, signal }),
    queryConfig: {
      enabled: workspaceId != null && !skipStates.includes(accountState),
      retry: false,
      select: (data) => data.mail_servers.length > 0,
    },
  });
}
